import { Link } from 'react-router-dom';
import Atol from '/Atol.svg';
import TravelAware from '/travel_aware.webp';
import Payment from '/Payment-Protect.png';
import WhatsApp from '../assets/social/whatsapp.svg';
import Youtube from '../assets/social/youtube.svg';
import Tiktok from '../assets/social/tiktok.svg';
import Facebook from '../assets/social/facebook.svg';
import Instagram from '../assets/social/instagram.svg';
import CountrySelect from "../components/Site/CountrySelect";
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { SubscribeFormData } from '../../../backend/src/shared/types';
import { sendSubscribeEmail } from '../api-client';

interface FormErrors {
  name?: string;
  email?: string;
}

const Footer: React.FC = () => {
  const [formData, setFormData] = useState<SubscribeFormData>({
    name: '',
    email: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: undefined
      }));
    }
  };

  const removeHomeHeightClass = () => {
    const homeElement = document.querySelector('.hidden-section');
    if (homeElement) {
      homeElement.classList.remove('hidden-section');
    }
  };

  useEffect(() => {
    removeHomeHeightClass();
  }, []);

  const currentYear = new Date().getFullYear();
  const socialLinks = [
    {
      id: 1,
      link: "https://wa.me/+447712279245",
      imgSrc: WhatsApp,
      alt: "whatsapp",
      label: "WhatsApp"
    },
    {
      id: 2,
      link: "https://www.youtube.com/@haloholidays",
      imgSrc: Youtube,
      alt: "youtube",
      label: "YouTube"
    },
    {
      id: 3,
      link: "https://www.tiktok.com/@haloholidays.uk",
      imgSrc: Tiktok,
      alt: "tiktok",
      label: "Tiktok"
    },
    {
      id: 4,
      link: "https://www.facebook.com/haloholidays.co.uk",
      imgSrc: Facebook,
      alt: "facebook",
      label: "Facebook"
    },
    {
      id: 5,
      link: "https://www.instagram.com/haloholidays.uk/",
      imgSrc: Instagram,
      alt: "instagram",
      label: "Instagram"
    }
  ];

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    return newErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
    } else {
      setLoading(true);
      try {
        // Send email using API client function
        await sendSubscribeEmail(formData);
        alert('Subscribed!');
        setFormData({
          name: '',
          email: ''
        });
      } catch (error) {
        alert('Failed to send message');
        console.error('Error sending message:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <footer aria-label="Site Footer" className="bg-white hidden-section">
      <div className="container mx-auto px-4 pt-8 pb-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-4 items-center">
          <hr className='border-gray-100 md:mb-6 mb-0' />
          <div className="container mx-auto px-0 flex flex-col items-start space-y-4 lg:flex-row lg:space-y-0 lg:space-x-4 md:items-center">
            <img src='/general/round-logo-new.webp' alt='footer logo' width='80px' height='80px' loading='lazy' className='' />
            <div className='md:w-5/12 2xl:w-7/12'>
              <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
                Are You <span className='text-halo-pink'>New</span> Here? <br />Get <span className='text-halo-pink'>£60</span> Off Your First Booking - No Strings Attached
              </h2>
              <p className="mt-2 text-gray-500">
                Sign up and receive your redeemable voucher instantly
              </p>
            </div>

            <form className="w-full" onSubmit={handleSubmit}>
              <label htmlFor="UserEmail" className="sr-only">
                email
              </label>
              <div className="border-gray-200 md:flex md:items-center md:gap-4">
                <div className="w-full flex flex-col md:flex-row items-center">
                  <input
                    type="text"
                    id="userName"
                    name="name"
                    placeholder="Name"
                    className="w-full h-[40px] focus:ring-transparent text-sm p-4 focus:outline-none border mb-3 md:mb-0 md:mr-3"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <input
                    type="email"
                    id="userEmail"
                    name="email"
                    placeholder="Email"
                    className="w-full h-[40px] focus:ring-transparent text-sm p-4 focus:outline-none border mb-3 md:mb-0 md:ml-3"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    className={`w-full h-[40px] md:ml-4 bg-halo-pink px-6 py-2 text-sm font-bold uppercase tracking-wide text-white transition-none hover:bg-pink-800 md:mt-0 md:w-auto md:shrink-0 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-label="Subscribe"
                    disabled={loading}
                  >
                    Subscribe & Save £60
                  </button>
                </div>
              </div>
            </form>

          </div>
        </div>

        <div className="md:mt-12 mt-2 border-t border-gray-100 pt-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-6">
            <div>
              <div className="text-lg font-medium">Contact Us</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li>Customer Care</li>
                <li><a href="tel:02072905570" className="text-halo-pink hover:text-blue-500 text-[16px] font-medium">020 7290 5570</a></li>
                <li><a href="tel:02072905578" className="text-halo-pink hover:text-blue-500 text-[16px] font-medium">020 7290 5578</a></li>
                <li>Complaints</li>
                <li><a href="tel:02072905572" className="text-halo-pink hover:text-blue-500 text-[16px] font-medium">020 7290 5572</a></li>
                <li>Need live support?</li>
                <li><a href="mailto:contact@haloholidays.co.uk" className="text-halo-pink hover:text-blue-500 text-[16px] font-medium">contact@haloholidays.co.uk</a></li>
              </ul>
            </div>

            <div>
              <div className="text-lg font-medium">Company</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li><Link to="/about-us" className="text-gray-700 hover:text-halo-pink">About Us</Link></li>
                <li><Link to="/blogs" className="text-gray-700 hover:text-halo-pink">Blogs</Link></li>
                <li><Link to="/destinations" className="text-gray-700 hover:text-halo-pink">Destinations</Link></li>
              </ul>
            </div>

            <div>
              <div className="text-lg font-medium">Support</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li><Link to="/contact-us" className="text-gray-700 hover:text-halo-pink">Contact</Link></li>
                <li><Link to="/terms" className="text-gray-700 hover:text-halo-pink">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-700 hover:text-halo-pink">Cookie Policy</Link></li>
                <li><Link to="/refund-policy" className="text-gray-700 hover:text-halo-pink">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="text-gray-700 hover:text-halo-pink">Cancellation Policy</Link></li>
                <li><Link to="/sitemap" className="text-gray-700 hover:text-halo-pink">Sitemap</Link></li>
              </ul>
            </div>

            <div>
              <div className="text-lg font-medium">Holidays</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li><Link to="/all-inclusive-holidays" className="text-gray-700 hover:text-halo-pink">All Inclusive Holidays</Link></li>
                <li><Link to="/beach-holidays" className="text-gray-700 hover:text-halo-pink">Beach Holidays</Link></li>
                <li><Link to="/couple-holidays" className="text-gray-700 hover:text-halo-pink">Couple Holidays</Link></li>
                <li><Link to="/family-holidays" className="text-gray-700 hover:text-halo-pink">Family Holidays</Link></li>
                <li><Link to="/honeymoon-holidays" className="text-gray-700 hover:text-halo-pink">Honeymoon Holidays</Link></li>
                <li><Link to="/luxury-holidays" className="text-gray-700 hover:text-halo-pink">Luxury Holidays</Link></li>
                <li><Link to="/last-minute-holidays" className="text-gray-700 hover:text-halo-pink">Last Minute Holidays</Link></li>
              </ul>
            </div>

            <div>
              <div className="text-lg font-medium">Other Branch</div>
              <ul className="mt-4 space-y-2 text-sm">
                <li>
                  <a
                    href="https://www.haloholidays.us/"
                    className="text-halo-pink hover:text-blue-500 text-[16px] font-medium"
                  >
                    Halo Holidays US
                  </a>
                </li>
                <li className="text-gray-700">
                  256 Chapman Rd, Newark, DE 19702, United States
                </li>
                <li className="font-medium text-gray-900">Registration No.</li>
                <li className="text-gray-700">FN 6373544</li>
              </ul>
            </div>

            <div>
              <div className="text-lg font-medium">Book with Confidence</div>
              <div className="flex mt-4 items-center"> {/* Added items-center to vertically center items */}
                <div style={{ width: '50px' }}>
                  <img src={Atol} alt='Atol' width="50" height="50" loading='lazy' />
                </div>
                <div className='-mt-1'>
                  <img src={Payment} alt='Payment Protection' width="120" height="80" loading='lazy' />
                </div>
                <div style={{ width: '80px' }} className='md:hidden'>
                  <img src={TravelAware} alt='Atol' width="70" height="60" loading='lazy' />
                </div>
              </div>
              <div className="hidden md:block items-center"> {/* Added items-center to vertically center items */}
                <div style={{ width: '80px' }}>
                  <a href="https://travelaware.campaign.gov.uk/">
                    <img src={TravelAware} alt='Atol' width="70" height="60" loading='lazy' />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-100 pt-4 text-center sm:text-left">
          <div className="sm:flex sm:justify-between md:flex md:justify-between">
            <p className="text-xs text-gray-500 text-[14.5px]">
              &copy; {currentYear} by <a href='/' className='hover:text-blue-500'>Halo Travels and Tours UK</a> All rights reserved.
            </p>
            <div className="flex gap-x-5 items-center justify-center md:mt-0 mt-4">
              {socialLinks.map(link => (
                <a key={link.id} href={link.link} target="_blank" rel="noopener noreferrer" aria-label={link.label} className="social-icon">
                  <img src={link.imgSrc} alt={link.alt} className="w-3.5 h-3.5" loading="lazy" />
                </a>
              ))}
              <div className='ml-4'>
                <CountrySelect />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;